import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { InboxEntry, InboxSection } from "../../../api/content/reviewCollaborationApi";
import { reviewAddress } from "./reviewAddress";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { readableChangeSetTitle } from "../../changeSets/changeSetTitle";
import { useMemberNames, type MemberNames } from "../../threads/data/useMemberNames";
import Chip from "../../../ui/feedback/Chip";
import type { ChipTone } from "../../../ui/styles/chipStyles";

/**
 * The review inbox's rows (curation-ui.md CU-10), for the Threads sidebar, which is the inbox
 * (ADR-0031 D4; Johannes, 29 Oct: "The sidebar IS the Inbox"). Each row opens the Thread step
 * that decides it and says why it is here.
 */
export function InboxRows({ projectId, section, entries, basisUpdateId = null }: { projectId: string; section: InboxSection; entries: readonly InboxEntry[]; basisUpdateId?: string | null }) {
  const people = useMemberNames(projectId);
  if (entries.length === 0) return null;
  return (
    <ul className="space-y-1 px-2 py-1">
      {entries.map((entry) => (
        <InboxRow key={entry.review.changeSet.changeSetId} section={section} entry={entry} people={people} basisUpdate={entry.review.changeSet.changeSetId === basisUpdateId} />
      ))}
    </ul>
  );
}

function InboxRow({ section, entry, people, basisUpdate }: { section: InboxSection; entry: InboxEntry; people: MemberNames; basisUpdate: boolean }) {
  const { t, i18n } = useTranslation();
  const changeSet = entry.review.changeSet;
  const effect = section === "myOpenProposals" ? t("reviews.index.items", { count: entry.review.touchedCount })
    : t(`reviews.collaboration.inbox.effect.${section === "sentBackToMe" ? "revise" : basisUpdate && entry.toApprove ? "basis" : entry.toApprove ? "approve" : "review"}`, { count: entry.review.touchedCount });
  const meta = [formatTimeAgo(changeSet.openedAt, i18n.resolvedLanguage), effect];
  return (
    <li>
      <Link to={reviewAddress(entry.review)} className="app-inbox-row app-card block space-y-1 p-2">
        <span className="app-title block min-w-0 text-sm font-semibold">{readableChangeSetTitle(changeSet.title)}</span>
        <span className="app-text-muted block text-xs first-letter:uppercase">{meta.join(", ")}</span>
        <Reasons section={section} entry={entry} people={people} />
      </Link>
    </li>
  );
}

/** Why the change set is in this section, in words, worst first. */
function Reasons({ section, entry, people }: { section: InboxSection; entry: InboxEntry; people: MemberNames }) {
  const { t } = useTranslation();
  const review = entry.review;
  const conflicts = review.conflictCounts?.conflict ?? 0;
  const chips: { readonly text: string; readonly tone: ChipTone }[] = [];
  if (section === "waitingForMe" && entry.toApprove) {
    chips.push({ text: t("reviews.collaboration.inbox.toApprove"), tone: "info" });
  }
  if (conflicts > 0) {
    chips.push({ text: t("reviews.index.conflicts", { count: conflicts }), tone: "danger" });
  }
  if (review.checkStatus === "fail" || review.checkStatus === "cannot_run") {
    chips.push({ text: t(`reviews.collaboration.inbox.checks.${review.checkStatus}`), tone: "warning" });
  }
  if (section === "myOpenProposals") {
    chips.push(
      review.uncoveredCount > 0
        ? { text: t("reviews.collaboration.inbox.waitingApprovals", { count: review.uncoveredCount }), tone: "neutral" }
        : { text: t("reviews.collaboration.inbox.approved"), tone: "success" },
    );
  }
  if (entry.openCandidateCount > 0) {
    chips.push({ text: t("reviews.collaboration.inbox.duplicates", { count: entry.openCandidateCount }), tone: "warning" });
  }
  return (
    <>
      {chips.length > 0 ? (
        <span className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <Chip key={chip.text} tone={chip.tone}>
              {chip.text}
            </Chip>
          ))}
        </span>
      ) : null}
      {entry.sentBack !== null ? (
        <span className="app-inbox-note block text-xs">
          <span className="font-semibold">{t("reviews.collaboration.inbox.sentBackBy", { name: people.nameOf(entry.sentBack.actor) })}</span> {entry.sentBack.reason}
        </span>
      ) : null}
      {section === "waitingForMe" && entry.requests.length > 0 ? (
        <span className="app-text-muted block text-xs">{t("reviews.collaboration.inbox.askedBy", { names: entry.requests.map((request) => people.nameOf(request.askedBy)).join(", ") })}</span>
      ) : null}
    </>
  );
}
